import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Badge, Text } from '@/components/common';
import { useViewerLocation } from '@/hooks';
import { UserProfile } from '@/types';
import { calculateDistance } from '@/utils/geo';

function getLabel(dist: number | null, t: TFunction<'discover'>): string {
  if (dist == null) return t('userCard.distance.withinCity');
  if (dist < 0.5) return t('userCard.distance.underHalfKm');
  return t('userCard.distance.approx', { distance: dist });
}

interface DistanceLabelProps {
  latitude?: number;
  longitude?: number;
  variant?: 'solid' | 'ghost';
}

export function DistanceLabel({ latitude, longitude, variant = 'solid' }: DistanceLabelProps) {
  const { t } = useTranslation('discover');
  const { location, locationEnabled } = useViewerLocation();

  if (!locationEnabled) return null;

  const dist = calculateDistance(location.latitude, location.longitude, latitude, longitude);

  const label = getLabel(dist, t);

  if (variant === 'ghost') {
    return (
      <Text size="1" color="gray">
        {label}
      </Text>
    );
  }

  return (
    <Badge color="black" highContrast>
      {label}
    </Badge>
  );
}

interface UserDistanceLabelProps {
  user: UserProfile;
  variant?: 'solid' | 'ghost';
}

export function UserDistanceLabel({ user, variant = 'solid' }: UserDistanceLabelProps) {
  return <DistanceLabel latitude={user.latitude} longitude={user.longitude} variant={variant} />;
}
